import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { X, Users } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

/**
 * SignupBanner — sticky promotional bar above the hero pitching registration
 * to anonymous visitors.
 *
 * Visibility rules:
 *  - Hidden once a user is logged in (no point pitching to existing users).
 *  - Dismissible via the ×; choice persisted to localStorage.
 *
 * Visuals: subtle navy gradient with an orange CTA pill. Two-line layout
 * with a live "X divers already registered" social-proof counter below the
 * primary message.
 */
const STORAGE_KEY = 'jd_signup_banner_dismissed_v1';
const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export const SignupBanner = () => {
  const { user } = useAuth();
  const [dismissed, setDismissed] = useState(true); // start hidden to avoid flash
  const [diverCount, setDiverCount] = useState(null);

  useEffect(() => {
    try {
      setDismissed(localStorage.getItem(STORAGE_KEY) === '1');
    } catch (_e) {
      setDismissed(false);
    }
  }, []);

  useEffect(() => {
    // Only fetch when banner is actually going to render — saves a request
    // on every page load for logged-in / dismissed users.
    if (user || dismissed) return;
    let active = true;
    axios.get(`${API_URL}/stats/registered-divers`).then((r) => {
      if (active) setDiverCount(r.data?.count ?? null);
    }).catch(() => { /* silent — counter just won't show */ });
    return () => { active = false; };
  }, [user, dismissed]);

  if (user || dismissed) return null;

  const handleDismiss = () => {
    try {
      localStorage.setItem(STORAGE_KEY, '1');
    } catch (_e) { /* ignore */ }
    setDismissed(true);
  };

  const formattedCount = diverCount != null
    ? diverCount.toLocaleString('en-US')
    : null;

  return (
    <div
      className="relative bg-gradient-to-r from-[#0A2342] via-[#0E2D55] to-[#0A2342] text-white"
      data-testid="signup-banner"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-5 flex items-center gap-4 sm:gap-6">
        <div className="flex-1 min-w-0">
          <p className="text-sm sm:text-base leading-snug">
            <span className="font-bold">Register now</span>
            <span className="opacity-95"> to unlock a host of Just Dive info on trips, specials, courses &amp; brand-new gear — plus </span>
            <span className="font-bold text-[#FFB084]">10% off selected items</span>
            <span className="opacity-70 hidden sm:inline">. T&amp;Cs apply.</span>
          </p>
          {formattedCount && (
            <p
              className="text-[11px] sm:text-xs text-white/75 mt-1.5 flex items-center gap-1.5"
              data-testid="signup-banner-divers-count"
            >
              <Users className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
              <span>
                <span className="font-semibold text-[#FFB084]">{formattedCount}</span> divers already registered
              </span>
            </p>
          )}
        </div>
        <Link
          to="/auth?mode=register"
          className="shrink-0 inline-flex items-center gap-1.5 bg-[#FF6B35] hover:bg-[#FF824F] active:bg-[#E55A26] text-white text-sm sm:text-base font-semibold px-5 sm:px-7 py-2.5 sm:py-3 rounded-full transition-colors shadow-md hover:shadow-lg whitespace-nowrap"
          data-testid="signup-banner-cta"
        >
          Sign Up Free
          <span aria-hidden="true">→</span>
        </Link>
        <button
          type="button"
          onClick={handleDismiss}
          aria-label="Dismiss"
          className="shrink-0 -mr-1 p-1.5 rounded-full text-white/60 hover:text-white hover:bg-white/10 transition-colors"
          data-testid="signup-banner-dismiss"
        >
          <X className="w-4 h-4 sm:w-5 sm:h-5" />
        </button>
      </div>
    </div>
  );
};

export default SignupBanner;
